import React, {FormEventHandler, useEffect, useRef, useState} from 'react';
import {loadStripe, Stripe, StripeElements} from '@stripe/stripe-js';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@common/core/settings/use-settings';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {useSelectedLocale} from '@common/i18n/selected-locale';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from '@common/ui/toast/toast';
import {
  confirmGuestTransferAddonPurchase,
  confirmTransferAddonPurchase,
  createGuestTransferAddonCheckout,
  createTransferAddonCheckout,
  type TransferAddonSku,
} from './transfer-addon-api';
import {DriveQueryKeys} from '../drive/drive-query-keys';

interface TransferAddonCheckoutDialogProps {
  open: boolean;
  sku: TransferAddonSku;
  price: string;
  limit: string;
  /** When set, checkout is charged to this guest email (no account). */
  guestEmail?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function TransferAddonCheckoutDialog({
  open,
  sku,
  price,
  limit,
  guestEmail,
  onClose,
  onSuccess,
}: TransferAddonCheckoutDialogProps) {
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const isDarkMode = useIsDarkMode();
  const {localeCode} = useSelectedLocale();
  const paymentElementRef = useRef<HTMLDivElement>(null);
  const initKeyRef = useRef<string | null>(null);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const settings = useSettings();
  const siteName = settings.branding?.site_name ?? '';
  const stripePublicKey = settings.billing?.stripe_public_key;
  const stripeEnabled = !!settings.billing?.stripe?.enable;
  const normalizedGuestEmail = guestEmail?.trim().toLowerCase() || '';
  const isGuestCheckout = !!normalizedGuestEmail;
  const billingEmail = isGuestCheckout ? normalizedGuestEmail : user?.email;

  useEffect(() => {
    if (!open) {
      initKeyRef.current = null;
      setStripe(null);
      setElements(null);
      setInitError(null);
      setErrorMessage(null);
      setIsSubmitting(false);
      return;
    }

    if (!stripeEnabled || !stripePublicKey) {
      setInitError('Stripe billing is not enabled.');
      return;
    }

    if (isGuestCheckout && !normalizedGuestEmail) {
      setInitError('Enter your email before buying more transfer.');
      return;
    }

    const initKey = `${isGuestCheckout ? normalizedGuestEmail : 'user'}-${sku}-${open}`;
    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    let cancelled = false;

    const checkoutPromise = isGuestCheckout
      ? createGuestTransferAddonCheckout(normalizedGuestEmail, sku)
      : createTransferAddonCheckout(sku);

    Promise.all([
      loadStripe(stripePublicKey, {
        apiVersion: '2022-08-01',
        locale: localeCode as any,
      }),
      checkoutPromise,
    ])
      .then(([stripeInstance, {clientSecret}]) => {
        if (cancelled || !stripeInstance || !paymentElementRef.current) {
          return;
        }

        const stripeElements = stripeInstance.elements({
          clientSecret,
          appearance: {theme: isDarkMode ? 'night' : 'stripe'},
        });

        const paymentElement = stripeElements.create('payment', {
          business: {name: siteName},
          terms: {card: 'never'},
          layout: {type: 'tabs', defaultCollapsed: false},
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
          defaultValues: {
            billingDetails: {email: billingEmail},
          },
        });
        paymentElement.mount(paymentElementRef.current);

        setStripe(stripeInstance);
        setElements(stripeElements);
        setInitError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setInitError(
          err?.response?.data?.message ||
            err?.message ||
            'Could not load checkout.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    sku,
    stripeEnabled,
    stripePublicKey,
    isDarkMode,
    localeCode,
    siteName,
    billingEmail,
    isGuestCheckout,
    normalizedGuestEmail,
  ]);

  const handleSubmit: FormEventHandler = async e => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        if (result.error.type !== 'validation_error') {
          setErrorMessage(result.error.message || 'Payment failed.');
        }
        return;
      }

      const paymentIntentId = result.paymentIntent?.id;
      if (!paymentIntentId || result.paymentIntent.status !== 'succeeded') {
        setErrorMessage('Payment was not completed.');
        return;
      }

      if (isGuestCheckout) {
        await confirmGuestTransferAddonPurchase(
          normalizedGuestEmail,
          paymentIntentId,
        );
        await queryClient.invalidateQueries({
          queryKey: ['send-transfer-limit', 'guest', normalizedGuestEmail],
        });
      } else {
        await confirmTransferAddonPurchase(paymentIntentId);
        await queryClient.invalidateQueries({queryKey: ['send-transfer-limit']});
        await queryClient.invalidateQueries(DriveQueryKeys.fetchStorageSummary);
      }

      toast.positive(
        isGuestCheckout
          ? 'Transfer limit increased for this email.'
          : 'Transfer limit increased for this month.',
      );
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message || 'Payment could not be completed.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center bg-[rgba(83,83,83,0.2)] px-[12px]">
      <div className="relative w-full max-w-[400px] rounded-[20px] border border-divider bg-[#f8f9ff] shadow-[0_8px_32px_rgba(55,55,55,0.18)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[10px] top-[10px] flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[#d8562a] text-[18px] leading-none text-white"
          aria-label="Close transfer limit checkout"
        >
          ×
        </button>
        <form onSubmit={handleSubmit} className="px-[14px] pb-[12px] pt-[10px]">
          <div className="pr-[26px] text-center text-[14px] font-bold leading-tight text-black">
            Paying {price} for {limit} Transfer Limit
          </div>
          {isGuestCheckout && (
            <p className="mt-[4px] text-center text-[12px] text-muted">
              Applied to {normalizedGuestEmail}
            </p>
          )}
          <div
            ref={paymentElementRef}
            className="mt-[8px] min-h-0 rounded-[12px] bg-paper p-[4px]"
          />
          {initError && (
            <p className="mt-[6px] text-center text-[12px] font-medium text-[#E34444]">
              {initError}
            </p>
          )}
          {errorMessage && (
            <p className="mt-[6px] text-center text-[12px] font-medium text-[#E34444]">
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={!stripe || !elements || !!initError || isSubmitting}
            className="mt-[8px] h-[40px] w-full rounded-[16px] bg-primary text-[14px] font-medium text-white disabled:opacity-60"
          >
            {isSubmitting ? 'Processing…' : 'Pay now'}
          </button>
        </form>
      </div>
    </div>
  );
}
